import DeleteIcon from '@mui/icons-material/Delete';
import ViewListIcon from '@mui/icons-material/ViewList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import type { FC } from 'react';

import CircleLoader from 'components/CircleLoader/CircleLoader';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Popup from 'components/Popup/Popup';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import useViewScheduleFilesPopup from './hooks/useViewScheduleFilesPopup';
import {
  AttachmentNameStyled,
  DateStyled,
  DateTitleStyled,
  FlexStyled,
  ListItemStyled,
  ListStyled,
  getPopupContentStyle,
} from './ViewScheduleFilesPopup.styled';

export type ViewScheduleFilesPopupPropsT = {
  rooftopId: string;
  isOpen: boolean;
  onClose: NoopT;
};

const ViewScheduleFilesPopup: FC<ViewScheduleFilesPopupPropsT> = ({
  rooftopId,
  isOpen,
  onClose,
}) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const {
    scheduleFiles,
    isLoadingScheduleFiles,
    isScheduleFilesFetchingError,
    handleOpenDeleteScheduleFilePopup,
    handleOpenScheduleFilePreview,
    handleOpenScheduleTicketsView,
  } = useViewScheduleFilesPopup({ rooftopId, isPopupOpened: isOpen });

  return (
    <Popup
      nested
      showCloseBtn
      modalTitle="Attached Schedule Files"
      size={ModalSizeE.VIEW_SCHEDULE_FILES}
      popupId={ScrollLockIdE.ViewScheduleFilesPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape={false}
    >
      {!!scheduleFiles?.length && !isLoadingScheduleFiles && (
        <>
          <ListStyled>
            {scheduleFiles?.map((scheduleFile, i) => {
              return (
                <ListItemStyled key={`schedule-file-${i}`}>
                  <FlexContainer>
                    <AttachmentNameStyled title={scheduleFile.fileName}>
                      {scheduleFile.fileName !== undefined ? scheduleFile.fileName : null}
                    </AttachmentNameStyled>
                  </FlexContainer>
                  <div>
                    <DateTitleStyled size="xs">Uploaded on</DateTitleStyled>
                    <DateStyled>
                      {format(new Date(scheduleFile.uploadedDateTime), 'MMM d, yyyy')}
                    </DateStyled>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Tooltip title="View tickets" arrow>
                      <IconButton
                        aria-label="View tickets"
                        onClick={(): void => handleOpenScheduleTicketsView(scheduleFile)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <ViewListIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View" arrow>
                      <IconButton
                        aria-label="View"
                        onClick={(): void => handleOpenScheduleFilePreview(scheduleFile)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <span>
                        <IconButton
                          aria-label="Delete"
                          disabled
                          onClick={handleOpenDeleteScheduleFilePopup(scheduleFile)}
                          sx={{ color: (t): string => t.palette.primary.main }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </ListItemStyled>
              );
            })}
          </ListStyled>
        </>
      )}

      {isScheduleFilesFetchingError && (
        <FlexContainer direction="column">
          <ErrorSection />
        </FlexContainer>
      )}

      {!scheduleFiles?.length && !isLoadingScheduleFiles && (
        <FlexStyled justify="center">
          <Typography variant="h2" color="darkBlue">
            Schedule files not found
          </Typography>
        </FlexStyled>
      )}

      {isLoadingScheduleFiles && (
        <FlexStyled align="center" justify="center">
          <CircleLoader />
        </FlexStyled>
      )}
    </Popup>
  );
};

export default ViewScheduleFilesPopup;
